import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { DraggableProvided } from "react-beautiful-dnd";

import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { Avatar, AvatarGroup, Box, Checkbox, IconButton, Stack, Typography } from "@mui/material";
import { DealRoleGuard, RoleComponentGuard } from "components/guards";
import { FilesPickerModal, FilesPickerMode } from "components/modals/Files-picker/FilesPicker";
import { FilesPickerInfoModal } from "components/modals/Files-picker/FilesPickerInfoModal";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { DocumentSectionType } from "constants/documents";
import { useRoleGuardHook } from "hooks";
import { useDealActions, useDealIdSelector, usePickUserPermissions } from "store";
import { DocumentQuestion, DocumentSection, DocumentSectionSharedUser, IFile, UserPermissionType } from "store/types";

import { RoleName } from "../../../constants";
import { useContentContext } from "../Content/content-context";
import { AddSectionContextMenu } from "../Menus/AddSectionContextMenu";
import { SectionContextMenu } from "../Menus/SectionContextMenu";
import { AIFileSection } from "./AIFileSection";
import { AIQuestionSection } from "./AIQuestionSection";
import { AnalyzeFundTableSection } from "./AnaltzeFundTableSection";
import { CashflowSection } from "./Cashflow-section/CashflowSection";
import { FileSection } from "./FileSection";
import { GraphSection } from "./Graph-section/GraphSection";
import { ImageSection } from "./ImageSection";
import { KPISection } from "./KPI-section/KPISection";
import { MeetingSection } from "./MeetingSection";
import { QuestionSection } from "./QuestionSection";
import { TableSection } from "./Table-section/TableSection";
import { TextSection } from "./Text-section/TextSection";
import { WebSearchSection } from "./WebSearchSection";

interface Props {
  section: DocumentSection;
  questions: DocumentQuestion[];
  draggableProvided: DraggableProvided;
  documentDealId: number | null;
  index: number;
}

interface ContentProps {
  section: DocumentSection;
  questions: DocumentQuestion[];
  onDelete: () => void;
  onShare: () => void;
  onAskAi: () => void;
  onFocus: () => void;
  onCreateSection: (data: {
    type: DocumentSectionType;
    question?: string;
    text?: string;
    isAiMeeting?: boolean;
  }) => Promise<DocumentSection | undefined>;
  setPendingAIQuestionIds: Dispatch<SetStateAction<number[]>>;
  pendingAIQuestionIds: number[];
  documentDealId: number | null;
}

const SectionContent: React.FC<ContentProps> = ({
  section,
  questions,
  onDelete,
  onShare,
  onAskAi,
  onFocus,
  onCreateSection,
  setPendingAIQuestionIds,
  pendingAIQuestionIds,
  documentDealId
}) => {
  switch (section.type) {
    case DocumentSectionType.Text:
      return (
        <TextSection
          section={section}
          onCreateSection={(data) => onCreateSection({ type: DocumentSectionType.Text, ...data })}
          onFocus={onFocus}
          documentDealId={documentDealId}
        />
      );
    case DocumentSectionType.Question:
      return (
        <QuestionSection
          id={section.id}
          documentId={section.document_id}
          sharedUsers={section.section_shared_users}
          onShare={onShare}
          onAskAi={onAskAi}
          onFocus={onFocus}
          setPendingAIQuestionIds={setPendingAIQuestionIds}
          pendingAIQuestionIds={pendingAIQuestionIds}
          questions={questions}
          section={section}
        />
      );
    case DocumentSectionType.AIQuestion:
      return (
        <AIQuestionSection
          id={section.id}
          documentId={section.document_id}
          sharedUsers={section.section_shared_users}
          onShare={onShare}
          onAskAi={onAskAi}
          onFocus={onFocus}
          setPendingAIQuestionIds={setPendingAIQuestionIds}
          pendingAIQuestionIds={pendingAIQuestionIds}
          questions={questions}
          section={section}
        />
      );

    case DocumentSectionType.Image:
      return <ImageSection section={section} onDelete={onDelete} onFocus={onFocus} />;
    case DocumentSectionType.File:
      return <FileSection section={section} onDelete={onDelete} onFocus={onFocus} />;
    case DocumentSectionType.AIFile:
      return (
        <AIFileSection
          section={section}
          onDelete={onDelete}
          onFocus={onFocus}
          setPendingAIQuestionIds={setPendingAIQuestionIds}
          pendingAIQuestionIds={pendingAIQuestionIds}
          questions={questions}
        />
      );
    case DocumentSectionType.Graph:
      return <GraphSection section={section} />;
    case DocumentSectionType.Table:
      return <TableSection section={section} />;
    case DocumentSectionType.KPI:
      return <KPISection section={section} />;
    case DocumentSectionType.Meeting:
      return <MeetingSection section={section} />;
    case DocumentSectionType.WebSearch:
      return <WebSearchSection section={section} />;
    case DocumentSectionType.CashflowGraph:
      return <CashflowSection section={section} />;
    case DocumentSectionType.AnalyzeFundTable:
      return <AnalyzeFundTableSection section={section} />;
    // case DocumentSectionType.RichText:
    //   return <RichTextSection section={section} />;
    default:
      return null;
  }
};

export const Section: React.FC<Props> = ({ section, draggableProvided, documentDealId, index, questions }) => {
  const {
    filesPickerModalOpen,
    setFilesPickerModalOpen,
    createSection,
    selectionMode,
    selectedSections,
    handleSelectSection,
    handleShare,
    handleMultiShare,
    handleSectionFocus,
    deleteSection,
    AIAnswerSection,
    isSectionMarkedForDrag
  } = useContentContext();

  const dealId = useDealIdSelector();
  const myDeals = usePickUserPermissions("deals");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;

  const sectionRef = useRef<HTMLDivElement>(null);
  const menuRef: React.RefObject<SVGSVGElement> = useRef(null);
  const addMenuRef: React.RefObject<HTMLButtonElement> = useRef(null);
  const [open, setOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [filesPickerInfoModal, setFilesPickerInfoModal] = useState<{
    infoModalOpen: boolean;
    isSendAiAvailable: boolean;
  } | null>(null);
  const [sectionHeight, setSectionHeight] = useState(0);
  const [pendingAIQuestionIds, setPendingAIQuestionIds] = useState<number[]>([]);

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    setOpen(true);
  };

  const Guard = dealId ? DealRoleGuard : RoleComponentGuard; //if user in deal , use DealRoleGuard, otherwise use RoleComponentGuard
  const draggableEnabled = useRoleGuardHook(true, [RoleName.Admin, RoleName.Analyst]);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setSectionHeight(entry.contentRect.height);
      }
    });

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const isSendAiAvailable = questions.some((question) => question.question);

  const { getFilesAction } = useDealActions();

  const isGlobalDocument = useMemo(() => !dealId, [dealId]);

  const filesPickerSubmit = async ({
    filesIds = [],
    files = [],
    attachments
  }: {
    filesIds?: number[];
    files?: IFile[];
    attachments: File[];
  }) => {
    await AIAnswerSection({
      file_ids: dealId ? filesIds : files.map((file) => file.id),
      files: attachments,
      sectionId: section.id,
      documentId: section.document_id,
      send_ai: isSendAiAvailable
    });
    setFilesPickerInfoModal({ infoModalOpen: true, isSendAiAvailable });
    !isGlobalDocument && dealId && (await getFilesAction(dealId));
  };

  return (
    <Box
      data-section-id={section.id}
      onFocus={() => handleSectionFocus(index)}
      sx={{
        position: "relative",
        pl: "16px",
        ":hover": {
          ".draggable-icon": { opacity: 1 },
          ".split": { opacity: 1 }
        },
        ".e-content": {
          minHeight: "24px !important",
          padding: "2px 0 !important",
          overflow: "visible"
        },
        ".e-disabled": { opacity: 1 },
        boxShadow: isSectionMarkedForDrag(section.id) ? "0 0 10px 0 rgba(113, 194, 235, 0.5)" : "none",
        background: "white !important"
      }}
      ref={draggableProvided.innerRef}
      {...draggableProvided.draggableProps}
    >
      <Stack direction="row" gap="10px" alignItems="flex-start" ref={sectionRef}>
        {!selectionMode && (
          <Guard
            availableRoles={[RoleName.Admin, RoleName.Analyst]}
            hideChild={!!noAccessPermission || !!isViewerPermission}
          >
            <Box
              sx={{
                opacity: 0,
                transition: "opacity 0.2s linear",
                display: "flex",
                alignItems: "center",
                height: "24px"
              }}
              className="draggable-icon"
              {...(draggableEnabled && { ...draggableProvided.dragHandleProps })}
              onContextMenu={handleContextMenu}
            >
              <DragIndicatorIcon sx={{ height: "19px" }} ref={menuRef} />
            </Box>
          </Guard>
        )}
        {selectionMode && (
          <Checkbox
            disableRipple
            className="documents__section-checkbox"
            checked={selectedSections.includes(section.id)}
            onChange={(_, checked) => handleSelectSection(section.id, checked)}
          />
        )}
        <SectionContent
          onShare={() => handleShare(section.id)}
          section={section}
          questions={questions}
          onDelete={() =>
            deleteSection({
              sectionId: section.id,
              isAiMeeting: section.type === DocumentSectionType.Meeting && section.meeting.is_ai
            })
          }
          onAskAi={() => setFilesPickerModalOpen(section.id)}
          onCreateSection={(data) => createSection({ position: index + 1, ...data })}
          onFocus={() => handleSectionFocus(index)}
          setPendingAIQuestionIds={setPendingAIQuestionIds}
          pendingAIQuestionIds={pendingAIQuestionIds}
          documentDealId={documentDealId}
        />
      </Stack>
      {Boolean(section?.section_shared_users?.length) &&
        section.type !== DocumentSectionType.Question &&
        section.type !== DocumentSectionType.AIQuestion && (
          <Stack>
            <AvatarGroup
              sx={{
                "& .MuiAvatar-root": { width: 24, height: 24, fontSize: 15 },
                marginRight: "10px"
              }}
              max={4}
            >
              {section.section_shared_users.map((share: DocumentSectionSharedUser, index: number) => (
                <MaterialTooltip isShow key={index} placement="top" title={share.user?.full_name}>
                  <Avatar src={share.user?.profile_photo_url} />
                </MaterialTooltip>
              ))}
            </AvatarGroup>
          </Stack>
        )}
      <Guard
        availableRoles={[RoleName.Admin, RoleName.Analyst]}
        hideChild={!!noAccessPermission || !!isViewerPermission}
        RenderInstead={<div style={{ width: "27px" }} />}
      >
        <IconButton
          className="split"
          sx={{
            opacity: Number(addMenuOpen),
            position: "absolute",
            ...((section.type === DocumentSectionType.Text && sectionHeight > 34) ||
            (section.type === DocumentSectionType.File && sectionHeight > 30) ||
            (section.type === DocumentSectionType.Meeting && sectionHeight > 30) ||
            (section.type === DocumentSectionType.WebSearch && sectionHeight > 30) ||
            (section.type === DocumentSectionType.Graph && sectionHeight > 30) ||
            (section.type === DocumentSectionType.Table && sectionHeight > 30) ||
            (section.type === DocumentSectionType.KPI && sectionHeight > 30)
              ? { left: "19px", bottom: "4.5px" }
              : { left: "4px", top: "4.5px" }),
            width: "15px",
            height: "15px",
            border: "1px solid #5d7782",
            transition: "opacity 0.2s linear",
            fontWeight: 300,
            p: 0,
            ...(addMenuOpen && { transform: "rotate(45deg)", backgroundColor: "#edf1f9" })
          }}
          onClick={() => setAddMenuOpen(true)}
          ref={addMenuRef}
        >
          <Typography fontSize={12}>+</Typography>
        </IconButton>
      </Guard>
      <SectionContextMenu
        anchorEl={menuRef.current}
        onClose={() => setOpen(false)}
        open={open}
        onDelete={() =>
          deleteSection({
            sectionId: section.id,
            isAiMeeting: section.type === DocumentSectionType.Meeting && section.meeting.is_ai
          })
        }
        onShare={() => {
          handleShare(section.id);
          setOpen(false);
        }}
        onMultiShare={() => {
          handleMultiShare();
          setOpen(false);
        }}
        onComment={() => {}}
        onDuplicate={() => {}}
      />
      <AddSectionContextMenu
        anchorEl={addMenuRef.current}
        open={addMenuOpen}
        onClose={() => setAddMenuOpen(false)}
        onSelect={(data) => {
          setAddMenuOpen(false);
          createSection({ position: index + 1, ...data });
        }}
        documentDealId={documentDealId}
      />
      {section.type === DocumentSectionType.AIQuestion && (
        <>
          {filesPickerModalOpen === section.id && (
            <FilesPickerModal
              currentFiles={section.ai_files || []}
              dealId={documentDealId}
              filesPickerModalOpen={filesPickerModalOpen === section.id}
              onClose={() => setFilesPickerModalOpen(null)}
              filesPickerSubmit={filesPickerSubmit}
              mode={FilesPickerMode.Document}
              title="Ask AI"
              workspaceTitle="Select or upload files"
              getFilesOutsideDeal={!dealId}
              acceptedFilesExtensions={[".pdf"]}
            />
          )}
          <FilesPickerInfoModal
            filesPickerInfoModal={filesPickerInfoModal}
            setFilesPickerInfoModal={setFilesPickerInfoModal}
          />
        </>
      )}
    </Box>
  );
};
